/**
 * clientErrorReporter — production-only frontend telemetry.
 *
 * Wires up two browser-level hooks to forward unhandled crashes to
 * `POST /api/client-errors`:
 *   - `window.onerror` for synchronous render / event-handler throws
 *   - `window.addEventListener('unhandledrejection', ...)` for async
 *     promise rejections (the exact case that bit us with the Invoices
 *     blank screen — the ErrorBoundary only catches sync render throws,
 *     so async crashes silently froze the page).
 *
 * Why we keep it simple:
 *   - No batching, no service worker, no Sentry SDK. ~50 LOC, zero deps.
 *   - Dedupe lives server-side (fingerprint + 5-min window).
 *   - We swallow our own errors so the reporter can never itself crash.
 *
 * Disabled when REACT_APP_BACKEND_URL is missing (e.g., unit tests).
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

let installed = false;
const recentlySent = new Map(); // local debounce per-tab (msg → ts)
const TAB_DEBOUNCE_MS = 30 * 1000;

function pickWorkspace() {
  try {
    return window.localStorage.getItem('currentWorkspaceId') || '';
  } catch {
    return '';
  }
}

function pickUserInfo() {
  // Best-effort — these keys are set by the auth flow when available.
  try {
    return {
      user_id: window.localStorage.getItem('user_id') || '',
      user_email: window.localStorage.getItem('user_email') || '',
    };
  } catch {
    return { user_id: '', user_email: '' };
  }
}

async function send(kind, message, stack) {
  if (!API_URL || !message) return;

  // Skip noisy / third-party errors that aren't actionable for us.
  // We check BOTH the message text and the stack trace because in-app
  // browser bridges (Facebook/Instagram/TikTok on Android/iOS) inject
  // their own scripts and their errors often carry a generic message
  // like "Error invoking postMessage" that only becomes identifiable
  // from the `iabjs://` / `ttwebview://` origin in the stack.
  const lowerMsg = String(message).toLowerCase();
  const lowerStack = String(stack || '').toLowerCase();
  if (
    // Same-origin CORS-masked errors — Chrome reports them as "Script error"
    // with no details; we cannot debug them anyway.
    lowerMsg.includes('script error') ||
    // ResizeObserver's benign "loop limit exceeded" fires constantly on
    // any page with responsive layouts; ignored by every serious tracker.
    lowerMsg.includes('resizeobserver') ||
    // Browser extensions injecting broken code into the page.
    lowerMsg.includes('chrome-extension://') ||
    lowerMsg.includes('moz-extension://') ||
    lowerStack.includes('chrome-extension://') ||
    lowerStack.includes('moz-extension://') ||
    lowerStack.includes('safari-extension://') ||
    // Sentry-style non-Error rejections without any info.
    lowerMsg.startsWith('non-error promise rejection captured') ||
    // ── Meta / Facebook / Instagram in-app browser (Android) ───────
    // Their "navigation_performance_logger" injects code that dies with
    // "Java object is gone" the instant the user backgrounds the app.
    // Not our bug, not fixable from our side — Meta's own script.
    lowerStack.includes('iabjs://') ||
    lowerMsg.includes('java object is gone') ||
    lowerMsg.includes('sendbeforeunloadmessage') ||
    lowerMsg.includes('senddatatonative') ||
    // ── Meta / Facebook / Instagram in-app browser (iOS) ───────────
    // WebKit bridge — same story, different platform. Their injected
    // navigation-performance script tries to talk to
    // `window.webkit.messageHandlers.<name>` but the object is
    // undefined once the in-app browser starts tearing down.
    lowerMsg.includes('window.webkit.messagehandlers') ||
    lowerMsg.includes('webkit.messagehandlers') ||
    lowerStack.includes('webkit.messagehandlers') ||
    // ── TikTok in-app browser ──────────────────────────────────────
    lowerStack.includes('ttwebview://') ||
    lowerStack.includes('bytedance-webview') ||
    // ── Snapchat / LinkedIn / other injected in-app-browser bridges
    lowerStack.includes('snapchat-inapp') ||
    lowerStack.includes('linkedin-inapp') ||
    // ── Third-party widgets injected into the page footer ─────────
    // (GoHighLevel / LeadConnector chat, Intercom-style loaders, etc.)
    // We can't fix errors inside their code and they shouldn't inflate
    // our production error inbox.
    lowerStack.includes('leadconnectorhq.com') ||
    lowerStack.includes('widgets.leadconnectorhq') ||
    // Google Translate messing with the DOM.
    lowerMsg.includes("cannot read properties of null (reading 'removechild')") ||
    lowerMsg.includes('null is not an object (evaluating')
  ) {
    return;
  }

  // Per-tab debounce so an error in a tight render loop doesn't spam.
  const key = `${kind}|${message.slice(0, 200)}`;
  const now = Date.now();
  const last = recentlySent.get(key) || 0;
  if (now - last < TAB_DEBOUNCE_MS) return;
  recentlySent.set(key, now);

  const { user_id, user_email } = pickUserInfo();
  const payload = {
    message: String(message).slice(0, 2000),
    stack: stack ? String(stack).slice(0, 8000) : '',
    url: window.location.href.slice(0, 500),
    user_agent: navigator.userAgent.slice(0, 500),
    workspace_id: pickWorkspace(),
    user_id,
    user_email,
    kind,
    client_ts: new Date().toISOString(),
  };

  try {
    // Fire-and-forget. `keepalive: true` lets the request survive
    // page-unloads (e.g., crash → navigate-away).
    await fetch(`${API_URL}/api/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    });
  } catch {
    // Reporter must never crash the app.
  }
}

export function installClientErrorReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const msg = event?.message || (event?.error && event.error.message) || 'window error';
    const stack = event?.error?.stack || '';
    send('error', msg, stack);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    let msg = 'unhandledrejection';
    let stack = '';
    if (reason instanceof Error) {
      msg = reason.message || String(reason);
      stack = reason.stack || '';
    } else if (typeof reason === 'string') {
      msg = reason;
    } else if (reason && typeof reason === 'object') {
      msg = reason.message || JSON.stringify(reason).slice(0, 500);
    }
    send('unhandledrejection', msg, stack);
  });
}
